import React, { useCallback, useState, useEffect } from "react";
import { BottomScrollListener } from "react-bottom-scroll-listener";
import { debounce } from "lodash";
import I18n from 'i18n.js.erb';
import {
  EXPANSIONS_KEY_FOR_SESSION_STORAGE,
  LOW_DEBOUNCE_TIME,
  handleBackPage,
  DESKTOP_WIDTH,
  enterDownHandler,
  parseBigInteger
} from "../../utils/utils";
import SwitchDisplay from "./SwitchDisplay";
import BannerSingleExpansion from "./BannerSingleExpansion";
import SubBannerExpansions from "./SubBannerExpansions";
import SubBannerExpansionsCompact from "./SubBannerExpansionsCompact";
import BannerMultipleExpansions from "./BannerMultipleExpansions";
import BannerMultipleExpansionsCompact from "./BannerMultipleExpansionsCompact";
import ErrorModal from "./ErrorModal";

const IndexExpansions = ({ game_id, game_name, originalExpansions, release_years, eras, allLocals }) => {

  const expansions = JSON.parse(originalExpansions);
  const [ enableFilters, setEnableFilters ] = useState(window.innerWidth >= DESKTOP_WIDTH)
  const [ showAllSubExpansions, setShowAllSubExpansions ] = useState(false)
  const [ initCompleted, setInitCompleted ] = useState(false)
  const [ expansionsToRender, setExpansionsToRender ] = useState([]);
  const initialFilters = { name: null, era_id: null, released_at: null, locals: null, sorting_direction: 'asc' }
  const [ filters, setFilters ] = useState(initialFilters);
  const [ page, setPage ] = useState(1);
  const [ hasLoadedAllData, setHasLoadedAllData ] = useState(false);
  const [ loading, setLoading ] = useState(false);
  const [ showErrorModal, setShowErrorModal ] = useState(false)
  const [ loadDataFromSessionStorage, setLoadDataFromSessionStorage ] = useState(false);
  const [ display, setDisplay ] = useState('list' );

  useEffect(() => {
    if (initCompleted && expansionsToRender.length === 0) {
      getExpansions();
    }
  }, [expansionsToRender]);

  useEffect(() => {
    if (!initCompleted) {
      setInitCompleted(true)

      let expansionsPrevData = sessionStorage.getItem(EXPANSIONS_KEY_FOR_SESSION_STORAGE);
      if (expansionsPrevData) {
        expansionsPrevData = JSON.parse(expansionsPrevData);
      }
      let expansionsCurrentUrl = `/games/${game_id}/expansions`;
      let functions = {
        setLoadDataFromSessionStorage: setLoadDataFromSessionStorage,
        setExpansionsToRender: setExpansionsToRender,
        setPage: setPage,
        setHasLoadedAllData: setHasLoadedAllData,
        setFilters: setFilters,
        setEnableFilters: setEnableFilters,
        setShowAllSubExpansions: setShowAllSubExpansions,
        setDisplay: setDisplay
      };
      if (expansionsPrevData && expansionsPrevData.url === expansionsCurrentUrl) {
        handleBackPage(EXPANSIONS_KEY_FOR_SESSION_STORAGE, expansionsPrevData, functions);
      } else {
        appendExpansions(expansions);
      }

      enterDownHandler(setEnableFilters)
    }
  }, []);

  useEffect(() => {
    if (filters.name) {
      debouncedUpdateFilterName();
      return debouncedUpdateFilterName.cancel;
    }
  }, [filters.name, debouncedUpdateFilterName]);


  // Funzione che permette di ottenere dal server nuove espansioni da renderizzare sull'interfaccia grafica
  const getExpansions = () => {
    if (!hasLoadedAllData && !loadDataFromSessionStorage) {
      let url = `/games/${game_id}/expansions.json?page=${page}`;
      Object.entries(filters).forEach(([fieldName, fieldValue]) => {
        if (fieldValue !== null)
          url += `&${fieldName}=${fieldValue}`
      })

      fetch(url, {
        method: "GET",
      })
        .then((response) => response.json())
        .then((newExpansions) => {
          setLoading(false)
          appendExpansions(newExpansions);
        })
        .catch((error) => {
          console.error("ERROR:", error);
          setShowErrorModal(true);
        });
    }
  };


  // Funzione che permette di concatenare le nuove espansioni ottenute del server all'array che contiene tutte le
  // espansioni da mostrare sull'interfaccia grafica all'utente
  const appendExpansions = (newExpansions) => {
    const newExps = showHideSubExpansions(newExpansions, !showAllSubExpansions, true)
    setExpansionsToRender(expansionsToRender.concat(newExps))
    setPage(page + 1);
    if (newExpansions.length < 4) {
      setHasLoadedAllData(true);
    }
  };


  // Funzione che permette di ottenere dal server le espansioni che matchano con i filtri
  // di ricerca specificati dall'utente sull'interfaccia grafica
  const getExpansionsByMatchingFilters = (filter, value) => {
    const newFilters = { ...filters }
    newFilters[filter] = value;
    updateFilters(newFilters)
  }


  //Funzione che esegue l'update debouncato del filtro di ricerca delle espansioni per nome
  const debouncedUpdateFilterName = useCallback(
    debounce(() => updateFilters(filters), LOW_DEBOUNCE_TIME),
    [filters.name]
  );


  //Funzione che setta il nuovo filtro di ricerca delle espansioni per nome
  function onChangeFilterNameHandler(newFilterName) {
    const newFilters = { ...filters }
    newFilters['name'] = newFilterName;
    setFilters(newFilters)
  }


  // Funzione che setta il nuovo filtro di ricerca delle espansioni per lingua
  function onChangeFilterLocalsHandler(newLocale) {
    let newFilterValue;
    if (!filters.locals)
      newFilterValue = [newLocale]
    else {
      if (!filters.locals.includes(newLocale))
        newFilterValue = [...filters.locals, newLocale];
      else
        newFilterValue = filters.locals.filter(locale => locale != newLocale)
    }
    getExpansionsByMatchingFilters('locals', newFilterValue)
  }


  // Funzione che aggiorna i filtri di ricerca delle espansioni e rilancia
  // la ricerca su quelle che matchano con i nuovi filtri specificati
  const updateFilters = (newFilters) => {
    window.scrollTo(0, 0)
    setFilters(newFilters)
    setLoading(true)
    setPage(1);
    setHasLoadedAllData(false)
    setExpansionsToRender([]);
  }


  // Funzione che gestisce il click sul banner di un'espansione / sottoespansione che compare sull'interfaccia grafica
  const handleClick = (e, expansion) => {
    if (!expansion.hide_sub_expansions && !expansion.has_translations) handleNavigation(e, expansion);
    else {
      handleShowSubExpansions(e, expansion);
    }
  };


  // Funzione che gestisce la navigazione alla pagina che contiene tutte le carte
  // dell'espansione cliccata sull'interfaccia grafica dell'utente
  const handleNavigation = (e, expansion) => {
    const expansionsPrevData = {
      url: `/games/${game_id}/expansions`,
      page: page,
      hasLoadedAllData: hasLoadedAllData,
      filters: filters,
      expansionsToRender: expansionsToRender,
      enableFilters: enableFilters,
      showAllSubExpansions: showAllSubExpansions,
      scrollY: window.scrollY,
      display: display
    };
    sessionStorage.setItem("expansionsPrevData", JSON.stringify(expansionsPrevData));

    window.location = `/games/${game_id}/expansions/${expansion.id}/blueprint_images?language=${expansion.language}`;
    e.stopPropagation();
  };


  // Funzione che consente la visualizzazione sull'interfaccia grafica di tutte le eventuali
  // sotto-espansioni / traduzioni in altre lingue di un'espansione cliccata dall'utente sull'interfaccia grafica
  const handleShowSubExpansions = (e, expansion) => {
    if (expansion.hide_sub_expansions) {
      const newExpansionsToRender = [];

      expansionsToRender.map((item) => {
        const newEra = {};

        Object.entries(item).map(([era, expansionsByEra]) => {
          newEra[era] = expansionsByEra.map((expByEra) => {
            const newExpansions = {};

            Object.entries(expByEra).map(([expansionsName, expansions]) => {
              newExpansions[expansionsName] = expansions.map((e) => {
                if (e.id === expansion.id) {
                  return {
                    ...e,
                    hide_sub_expansions: false
                  };
                }
                return e;
              });
            });
            return newExpansions;
          });
        });

        newExpansionsToRender.push(newEra);
      });

      setExpansionsToRender(newExpansionsToRender);
    }
  };

  // Funzione che consente di nascondere tutte le eventuali
  // sotto-espansioni / traduzioni in altre lingue di un'espansione cliccata dall'utente sull'interfaccia grafica
  const handleHideSubExpansions = (e, expansion) => {
    if (!expansion.hide_sub_expansions) {
      const newExpansionsToRender = [];

      expansionsToRender.map((item) => {
        const newEra = {};

        Object.entries(item).map(([era, expansionsByEra]) => {
          newEra[era] = expansionsByEra.map((expByEra) => {
            const newExpansions = {};

            Object.entries(expByEra).map(([expansionsName, expansions]) => {
              newExpansions[expansionsName] = expansions.map((e) => {
                if (e.id === expansion.id)
                  return {
                    ...e,
                    hide_sub_expansions: true,
                  };
                return e;
              });
            });
            return newExpansions;
          });
        });

        newExpansionsToRender.push(newEra);
      });

      setExpansionsToRender(newExpansionsToRender);
    }
  };

  // Funzione che restituisce un array contenente tutte le eventuali sottoespansioni / traduzioni in altre lingue
  // di un'espansione che è disponibile in edizioni differenti
  const flatSubExpansionsOrTranslations = (expansions, includeParentExpansion) => {
    let flatten_array = [];
    for (const expansion of expansions) {
      if (includeParentExpansion)
        flatten_array.push(expansion)
      if (expansion.sub_expansions && expansion.sub_expansions.length > 0)
        flatten_array = flatten_array.concat(expansion.sub_expansions);
      if (expansion.translations && expansion.translations.length > 0)
        flatten_array = flatten_array.concat(expansion.translations);
    }
    return flatten_array;
  };


  // Funzione che verifica se ci sia almeno un filtro attivo
  const isFilterEnabled = () =>{
    return (
      filters.name !== null ||
      filters.era_id !== null ||
      filters.released_at !== null ||
      filters.locals !== null
      ) ||
      filters.sorting_direction !== 'asc';
  };


  // Funzione che mostra / nasconde sull'interfaccia grafica tutti i sottobanner relativi alle (eventuali)
  // sotto-espansioni / traduzioni di ogni espansione renderizzata sulla pagina
  function showHideSubExpansions(expansions, show, returnExpansions) {
    const newExpansionsToRender = [];

    expansions.map((item) => {
      const newEra = {};

      Object.entries(item).map(([era, expansionsByEra]) => {
        newEra[era] = expansionsByEra.map((expByEra) => {
          const newExpansions = {};

          Object.entries(expByEra).map(([expansionsName, expansions]) => {
            newExpansions[expansionsName] = expansions.map((e) => {
              const expansion = {...e}
              if (initCompleted) {
                expansion["hide_sub_expansions"] =
                  expansions.length > 1 ||
                  (e.sub_expansions && e.sub_expansions.length > 0) ||
                  (e.translations && e.translations.length > 0) ? show : false;
              }

              return expansion;
            });
          });
          return newExpansions;
        });
      });

      newExpansionsToRender.push(newEra);
    });

    if (returnExpansions)
      return newExpansionsToRender
    else
      setExpansionsToRender(newExpansionsToRender);
  }


  const handleShowAllSubExpansions = () => {
    showHideSubExpansions(expansionsToRender, showAllSubExpansions);
    setShowAllSubExpansions(!showAllSubExpansions);
  }

  // Funzione che permette di cambiare la modalità di visualizzazione delle espansioni
  const switchTab = (e) => {
    e.stopPropagation()
    if (e.target.id === "button-table") {
      setDisplay("table");
    } else {
      setDisplay("list");
    }
  };

  return (
    <div className="container-fluid w-100 mt-5 mt-md-0">
      <div className="button-show-all-sub-expansion show-on-desktop-d-block minH-47px">
        <button type="button" className="btn" id="show_sub_banners_button" name="show_sub_banners_button"
                onClick={ handleShowAllSubExpansions }>
          <span>
            {
              showAllSubExpansions ?
                <i className="fa far fa-eye-slash icon-in-btn-size ml-8px text--size-27px-imp" />
              :
                <i className="fa fa-eye icon-in-btn-size ml-8px enable-filters-expansions-icon" />
            }
           </span>
        </button>
      </div>

      <div className="button-show-all-sub-expansion-on-mobile show-on-mobile-d-block">
        <button type="button" className="btn"
                onClick={ handleShowAllSubExpansions }>
          <span >
            {
              showAllSubExpansions ?
                <i className="fa far fa-eye-slash icon-in-btn-size ml-8px enable-filters-expansions-icon" />
              :
                <i className="fa fa-eye icon-in-btn-size ml-8px enable-filters-expansions-icon" />
              }
           </span>
        </button>
      </div>

      {
        enableFilters ?
          <React.Fragment>
            <div className="container filters-expansions-container shadow rounded form-group font-small show-on-desktop-d-block bg-light-gray-imp expansion--desktop--border-box top-170px">
              <div style={{ zIndex: "10" }} className="input-group">
                <div className="row">

                  <div className="col-12 text-right">
                    <button type="button" className="btn p-0"
                            onClick={ (e) => setEnableFilters(false) }>
                      <i className="fas fa-times" />
                    </button>
                  </div>
                  <div className="col-10">
                    <p className="text-weight-500 text-space text--size-09rem">
                      <i className="fa fa-search mr-1" />
                      {I18n.t('js.indexExpansionsPage.search')}:</p>
                  </div>
                </div>

                <input
                  type="text"
                  id="name_filter"
                  name="name_filter"
                  value={filters.name ? filters.name : ""}
                  className={`form-control ${filters.name ? 'border-aiG-blue-imp' : ''} rounded text--size-normal`}
                  placeholder={`${I18n.t('js.filters.expansionName')}`}
                  onChange={(e) => e.target.value !== "" ? onChangeFilterNameHandler(e.target.value) : updateFilters({ ...filters, name: "" })}
                />

                {
                  eras && eras.length > 0 &&
                    <select
                      className={`form-control ${filters.era_id ? 'border-aiG-blue-imp' : ''} rounded mt-3`}
                      value={filters.era_id ? filters.era_id : ""}
                      name="era_filter"
                      id="era_filter"
                      onChange={(e) => getExpansionsByMatchingFilters('era_id', e.target.value)}
                    >
                      <option value={""}>{I18n.t('js.filters.era')}</option>
                      {eras.map((era) => (
                        <option value={era.id} key={era.id}>
                          {era.name}
                        </option>
                      ))}
                    </select>
                }

                <select
                  className={`form-control ${filters.released_at ? 'border-aiG-blue-imp' : ''} rounded mt-3`}
                  value={filters.released_at ? filters.released_at : ""}
                  name="released_at_filter"
                  id="released_at_filter"
                  onChange={(e) => getExpansionsByMatchingFilters('released_at', e.target.value)}
                >
                  <option value={""}>{I18n.t('js.filters.releaseYear')}</option>
                  {release_years.map((release_year) => (
                    <option value={release_year[0]} key={release_year[0]}>
                      {release_year[0]}
                    </option>
                  ))}
                </select>

                {
                  allLocals && allLocals.length > 1 &&
                    <div className="row pt-4">
                      {allLocals.map((locale) => {
                        return (
                          <div key={locale} className="col-4 text-center">
                            <label
                              key={locale}
                              className=""
                            >
                              <input
                                type="checkbox"
                                value={locale}
                                checked={filters.locals && filters.locals.includes(locale)}
                                className="mt-0 mr-1 cursor-pointer"
                                id={'locale_' + locale}
                                name={'locale_' + locale}
                                onChange={(e) => onChangeFilterLocalsHandler(e.target.value) }
                              />
                              <img src={window.location.origin + `/images/flags/${locale}.png`}
                                   className="mb-2"
                                   style={{ maxHeight: "20px" }} alt="" />
                            </label>
                          </div>
                        )
                      })}
                    </div>
                }
              </div>

              <div className="pt-1 mt-3">
                <p className="text-weight-500 text-space text--size-09rem mb-0">{I18n.t('js.indexExpansionsPage.sortExpansion')}:</p>
                <button
                  type="button"
                  className="btn-standard bg-dark text-light mt-1 pt-1 pb-1 pr-5 pl-5 btn-standard-resized w-100"
                  onClick={(e) => getExpansionsByMatchingFilters('sorting_direction', filters.sorting_direction === 'asc' ? 'desc' : 'asc')}
                >
                  {filters.sorting_direction === 'asc' ? I18n.t('js.filters.erliest') : I18n.t('js.filters.latest')}
                </button>
              </div>

              <div className="pt-1 mt-3">
                <button
                  type="button"
                  className="btn-standard-outline mt-1 pt-1 pb-1 pr-5 pl-5 btn-standard-outline-resized w-100"
                  onClick={ (e) => updateFilters(initialFilters) } >
                  {I18n.t('js.filters.resetFilter')}
                </button>
              </div>

              <div className="pt-1 mt-3 mb-3 text-weight-500 text-space text--size-09rem">
                <a href={`/games/${game_id}/sealed`}>{I18n.t('js.expansionsPage.viewSealed')}</a>
              </div>
            </div>

            <div className="container shadow  form-group font-small w-100
                 show-on-mobile-d-block expansion__filter--mobile-position
                 col-12 text--size-normal bg-light-gray">
              <div style={{ zIndex: "10" }} className="w-100">
                <div className="col-12 text-right">
                  <button type="button" className="btn p-0 mt-8px mr-8px"
                          onClick={ (e) => setEnableFilters(false) }>
                    <i className="fas fa-times" />
                  </button>
                </div>

                <div className="col-10">
                  <p className="text-weight-500 text-space text--size-normal">
                  <i className="fa fa-search mr-1" />
                    {I18n.t('js.indexExpansionsPage.search')}:</p>
                </div>

                <div className="col-12">
                  <input
                    type="text"
                    value={filters.name ? filters.name : ""}
                    className={`form-control ${filters.name ? 'border-aiG-blue-imp' : ''} rounded text--size-normal mt-20px mb-20px expansion__filter-box--input-size`}
                    placeholder={`${I18n.t('js.filters.name')}`}
                    onChange={(e) => e.target.value !== "" ? onChangeFilterNameHandler(e.target.value) : updateFilters({ ...filters, name: "" })}

                  />

                  <select
                    className={`form-control ${filters.era_id ? 'border-aiG-blue-imp' : ''} mt-3 rounded text--size-normal mt-20px mb-20px expansion__filter-box--input-size`}
                    value={filters.era_id ? filters.era_id : ""}
                    name="era_filter"
                    id="era_filter"
                    onChange={(e) => getExpansionsByMatchingFilters('era_id', e.target.value)}
                  >
                    <option value={""}>{I18n.t('js.filters.era')}</option>
                    {eras.map((era) => (
                      <option value={era.id} key={era.id}>
                        {era.name}
                      </option>
                    ))}
                  </select>

                  <select
                    className={`form-control ${filters.released_at ? 'border-aiG-blue-imp' : ''} mt-3 rounded text--size-normal mt-20px mb-20px p-20px expansion__filter-box--input-size`}
                    value={filters.released_at ? filters.released_at : ""}
                    name="released_at_filter"
                    id="released_at_filter"
                    onChange={(e) => getExpansionsByMatchingFilters('released_at', e.target.value)}
                  >
                    <option value={""}>{I18n.t('js.filters.releaseYear')}</option>
                    {release_years.map((release_year) => (
                      <option value={release_year[0]} key={release_year[0]}>
                        {release_year[0]}
                      </option>
                    ))}
                  </select>

                  {
                    allLocals && allLocals.length > 1 &&
                      <div className="row pt-2">
                        {allLocals.map((locale) => {
                          return (
                            <div key={locale} className="col-4 text-center">
                              <label
                                key={locale}
                                className=""
                              >
                                <input
                                  type="checkbox"
                                  value={locale}
                                  checked={filters.locals && filters.locals.includes(locale)}
                                  className="mt-0 mr-1 cursor-pointer"
                                  id={'locale_' + locale}
                                  onChange={(e) => onChangeFilterLocalsHandler(e.target.value) }
                                />
                                <img src={window.location.origin + `/images/flags/${locale}.png`}
                                     className="mb-2"
                                     style={{ maxHeight: "20px" }} alt="" />
                              </label>
                            </div>
                          )
                        })}
                      </div>
                  }

                  <div className="pt-1 mt-3 d-flex">
                    <p className="text-weight-500 text-space text--size-normal w-40 expansion__filter-text--line-height d-flex align-items-center mb-0">{I18n.t('js.indexExpansionsPage.sortExpansion')}:</p>
                    <button
                      type="button"
                      className="btn-standard bg-dark text-light mt-1 pt-1 pb-1 pr-5 pl-5 btn-standard-resized w-60"
                      onClick={(e) => getExpansionsByMatchingFilters('sorting_direction', filters.sorting_direction === 'asc' ? 'desc' : 'asc')}
                    >
                      {filters.sorting_direction === 'asc' ? I18n.t('js.filters.latest') : I18n.t('js.filters.erliest')}
                    </button>
                  </div>

                  <div className="pt-1 mt-3 d-flex">
                    <button
                      type="button"
                      className="btn-standard-outline mt-1 pt-1 pb-1 mr-1 btn-standard-outline-resized w-100"
                      onClick={ (e) => {
                        updateFilters(initialFilters);
                        setEnableFilters(false);
                      } } >
                      {I18n.t('js.filters.resetFilter')}
                    </button>

                    <button
                      type="button"
                      className="btn-standard bg-dark text-light mt-1 pb-1 ml-1 btn-standard-outline-resized w-100"
                      onClick={ (e) => setEnableFilters(false) } >
                      {I18n.t('js.advancedSearch.filterComponent.search')}
                    </button>
                  </div>

                  <div className="pt-1 mt-3 mb-3 text-weight-500 text-space text--size-09rem">
                    <a href={`/games/${game_id}/sealed`}>{I18n.t('js.expansionsPage.viewSealed')}</a>
                  </div>
                </div>
              </div>
            </div>
          </React.Fragment>
         :
          <React.Fragment>
            <div className={`${isFilterEnabled() && 'icon--bg-active'} enable-filters-expansions-button rounded-right show-on-desktop p-5px top-170px`}>
              <button type="button" className="btn"
                      onClick={ (e) => setEnableFilters(true) }>
                <i className={`${isFilterEnabled()? 'icon--color-active' : 'icon-color-standard'} fas fa-filter enable-filters-expansions-icon`} />
              </button>
            </div>

            <div className={`${isFilterEnabled() && 'icon--bg-active'} rounded-right show-on-mobile expansion__filter--mobile-position`}>
              <button type="button" className="btn p-7px"
                      onClick={ (e) => setEnableFilters(true) }>
                <i className={`${isFilterEnabled()? 'icon--color-active' : 'icon-color-standard'} fas fa-filter enable-filters-expansions-icon`} />
              </button>
            </div>
          </React.Fragment>
      }


      {
        loading ?
          <div className="d-flex justify-content-center mt-5">
            <div className="d-flex align-items-center text-muted">
              <div className="spinner-border" role="status" />
              <b className="ml-2">{I18n.t('js.expansionsPage.loadingExpansions')}</b>
            </div>
          </div>
        :
          expansionsToRender.length > 0 ?
            <div className={`${enableFilters && 'expansion--mobile--mt'}`} >

              <div className="col-6 d-none d-lg-block text-right" style={{position: 'fixed', right: '0', zIndex: '20'}}>
                <SwitchDisplay switchTab={switchTab}
                               tableTabColor={`${display == 'table' ? 'icon--color-active icon--bg-active' : 'icon--color-standard'}`}
                               listTabColor={`${display != 'table' ? 'icon--color-active icon--bg-active' : 'icon--color-standard'}`}/>
              </div>

              {/*visualizzazione compatta*/}
              <div className={`${display == 'table' ? '' : 'd-none'}`}>
                <BottomScrollListener onBottom={getExpansions}>
                  {expansionsToRender.map((item) => {
                    return Object.entries(item).map(([era, expansionsByEra]) => {
                      return <div className="row ml-130px pb-3 mt-4" key={era}>
                        <div className="col-12 ml-auto title-eras text-night-blue text-center text-space mb-10px">{era}</div>
                        {expansionsByEra.map((expByEra) => {
                          return Object.entries(expByEra).map(
                            ([expansionsName, expansions]) => {
                              return (
                                <React.Fragment key={expansionsName}>
                                  {
                                    expansions.length === 1 ?
                                      <React.Fragment>
                                      {
                                        expansions[0].count_blueprint_images > 0 &&
                                          <React.Fragment>
                                            <div className="col-12 d-flex align-items-center border-bottom-dark
                                               minH-35px expansion-container-compact pr-0 pl-0"
                                                 onClick={(e) => handleClick(e, expansions[0])}>
                                              <div className="col-1 text-aiBlue">
                                                <b>{expansions[0].released_at.split('#')[0].toUpperCase()}</b>
                                              </div>
                                              <div className="col-3 text-truncate"><b>{(expansions[0].code ? expansions[0].code + ' - ' : '') + expansions[0].name}</b></div>
                                              <div className="col-4 text-truncate"><b>{expansions[0].en_name}</b></div>
                                              <div className="col-2">
                                                {
                                                  expansions[0].icons_urls.map((url, i) => {
                                                    return (
                                                      <div key={url + "_" + i} className="col-xs-2 col-lg-1 mr-1">
                                                        <img
                                                          src={url}
                                                          className="expansion__symbol--size img-size-logo-navbar"
                                                        />
                                                      </div>
                                                    );
                                                  })
                                                }
                                              </div>
                                              <div className="col-1 p-0 text-aiBlue">
                                                {
                                                  !(filters.locals && filters.locals.length > 0 && filters.locals.length < allLocals.length) &&
                                                  <small>
                                                    <span>
                                                      {I18n.t('js.expansionsPage.aigCard')}:&nbsp;
                                                      {parseBigInteger(expansions[0].graded_cards_population)}
                                                    </span>
                                                  </small>
                                                }
                                              </div>
                                              <div className="col-1 p-0 text-aiBlue">
                                                {
                                                  !(filters.locals && filters.locals.length > 0 && filters.locals.length < allLocals.length) &&
                                                  <small>
                                                    <span>
                                                      {I18n.t('js.expansionsPage.myCollection')}:&nbsp;
                                                      {expansions[0].blueprint_images_on_my_collection}/{expansions[0].count_blueprint_images}
                                                    </span>
                                                  </small>
                                                }
                                              </div>
                                            </div>

                                            {
                                              expansions[0].hide_sub_expansions === false &&
                                              ((expansions[0].sub_expansions &&
                                                  expansions[0].sub_expansions.length > 0) ||
                                                (expansions[0].translations &&
                                                  expansions[0].translations.length > 0)) && (
                                                <SubBannerExpansionsCompact
                                                  parent_expansion={expansions[0]}
                                                  sub_expansions={flatSubExpansionsOrTranslations(expansions, false)}
                                                  game_name={game_name}
                                                  handleNavigation={handleNavigation}
                                                  hasTranslations={expansions[0].has_translations}
                                                  handleHideSubExpansions={handleHideSubExpansions}
                                                />
                                              )
                                            }
                                          </React.Fragment>
                                      }
                                      </React.Fragment>
                                      :
                                      <React.Fragment>
                                        {
                                          expansions[0].count_blueprint_images > 0 &&
                                          <React.Fragment>
                                            <BannerMultipleExpansionsCompact
                                              key={expansionsName}
                                              expansions={expansions}
                                              handleShowSubExpansions={handleShowSubExpansions}
                                              hideCounts={filters.locals && filters.locals.length > 0 && filters.locals.length < allLocals.length}
                                            />
                                            {!expansions[0].hide_sub_expansions && (
                                              <SubBannerExpansionsCompact
                                                parent_expansion={expansions[0]}
                                                sub_expansions={flatSubExpansionsOrTranslations(expansions, true)}
                                                handleNavigation={handleNavigation}
                                                hasTranslations={expansions[0].has_translations}
                                                handleHideSubExpansions={handleHideSubExpansions}
                                              />
                                            )}
                                          </React.Fragment>
                                        }
                                      </React.Fragment>
                                  }
                                </React.Fragment>
                              );
                            });
                        })}
                      </div>
                    });
                  })}

                  {
                    !hasLoadedAllData && !loadDataFromSessionStorage && expansionsToRender.length > 0 ?
                      <React.Fragment>
                        <div className="d-flex justify-content-center mb-5">
                          <div className="d-flex align-items-center">
                            <div className="spinner-border" role="status" />
                            <b className="ml-2">{I18n.t('js.common.loading')}</b>
                          </div>
                        </div>
                      </React.Fragment>
                    :
                      <React.Fragment></React.Fragment>
                  }

                </BottomScrollListener>
              </div>

              {/*visualizzazione a lista*/}
              <div className={`${display == 'list' ? '' : 'd-none'}`}>
                <BottomScrollListener onBottom={getExpansions}>
                  {expansionsToRender.map((item) => {
                    return Object.entries(item).map(([era, expansionsByEra]) => {
                      return <div className="row mb-130px-imp mt-4" key={era}>
                        <div className="col-12 ml-auto title-eras text-night-blue text-center text-space mb-10px">{era}</div>
                        <div className="col-12 position-relative ">
                          {expansionsByEra.map((expByEra) => {
                            return Object.entries(expByEra).map(
                              ([expansionsName, expansions]) => {
                                return (
                                  <React.Fragment key={expansionsName}>
                                    {
                                      expansions.length === 1 ? (
                                        <React.Fragment>
                                        {
                                          expansions[0].count_blueprint_images > 0 &&
                                            <React.Fragment>
                                              <BannerSingleExpansion
                                                expansion={expansions[0]}
                                                game_name={game_name}
                                                hideCounts={filters.locals && filters.locals.length > 0 && filters.locals.length < allLocals.length}
                                                handleClick={handleClick}
                                              />
                                              {expansions[0].hide_sub_expansions === false &&
                                                ((expansions[0].sub_expansions &&
                                                    expansions[0].sub_expansions.length > 0) ||
                                                  (expansions[0].translations &&
                                                    expansions[0].translations.length > 0)) && (
                                                  <SubBannerExpansions
                                                    parent_expansion={expansions[0]}
                                                    sub_expansions={flatSubExpansionsOrTranslations(expansions, false)}
                                                    game_name={game_name}
                                                    handleNavigation={handleNavigation}
                                                    hasTranslations={expansions[0].has_translations}
                                                    handleHideSubExpansions={handleHideSubExpansions}
                                                  />
                                                ) }
                                            </React.Fragment>
                                        }
                                        </React.Fragment>
                                      ) : (
                                        <React.Fragment>
                                          {
                                            expansions[0].count_blueprint_images > 0 &&
                                              <React.Fragment>
                                                <BannerMultipleExpansions
                                                  key={expansionsName}
                                                  expansions={expansions}
                                                  hideCounts={filters.locals && filters.locals.length > 0 && filters.locals.length < allLocals.length}
                                                  handleShowSubExpansions={handleShowSubExpansions}
                                                />
                                                {!expansions[0].hide_sub_expansions && (
                                                  <SubBannerExpansions
                                                    parent_expansion={expansions[0]}
                                                    sub_expansions={flatSubExpansionsOrTranslations(expansions, true)}
                                                    handleNavigation={handleNavigation}
                                                    hasTranslations={expansions[0].has_translations}
                                                    handleHideSubExpansions={handleHideSubExpansions}
                                                  />
                                                )}
                                              </React.Fragment>
                                          }
                                        </React.Fragment>
                                      )
                                    }
                                  </React.Fragment>
                                );
                              }
                            );
                          })}
                        </div>
                      </div>
                    });
                  })}
                  {
                    !hasLoadedAllData && !loadDataFromSessionStorage && expansionsToRender.length > 0 ? (
                      <div className="d-flex justify-content-center mb-5">
                        <div className="d-flex align-items-center">
                          <div className="spinner-border" role="status" />
                          <b className="ml-2">{I18n.t('js.common.loading')}</b>
                        </div>
                      </div>
                    ) : (
                      <div />
                    )
                  }
                </BottomScrollListener>
              </div>
            </div>
          :
            <div className={`${enableFilters && 'expansion--mobile--mt'} pt-5`}>
                <p className="text-space text-black-50 text--size-normal text-center text-bold">{I18n.t('js.filters.noResultsFound')}</p>
            </div>
      }

      <ErrorModal
        showErrorModal={showErrorModal}
        setShowErrorModal={setShowErrorModal}
      />
    </div>
  );
}

export default IndexExpansions;